import React, { useCallback, useEffect, useState } from 'react';
import { getApiBase } from '../../lib/apiBase';
import { getDashboardAuthHeaders } from '../../lib/dashboardSession';

const ENV_LABELS = {
  ELEVENLABS_API_KEY: 'ElevenLabs API Key',
  ELEVENLABS_VOICE_ID: 'ElevenLabs Voice ID',
  AVATAR_ID: 'Avatar ID (LiveAvatar)',
  GOOGLE_SHEETS_WEB_APP_URL: 'Google Sheets Web App URL',
  GOOGLE_SHEETS_URL: 'Google Sheets URL',
  GOOGLE_SHEET_ID: 'Google Sheet ID (import)',
  VOICE_ID: 'Voice ID (LiveAvatar)',
  GOOGLE_GENAI_API_KEY: 'Google Gemini API Key',
  OPENAI_API_KEY: 'OpenAI API Key',
  REACT_APP_HEYGEN_API_KEY: 'HeyGen/LiveAvatar API Key',
  REACT_APP_LIVEAVATAR_API_KEY: 'LiveAvatar API Key (alt)',
  ADMIN_PIN: 'Admin PIN (legacy)',
  DASHBOARD_JWT_SECRET: 'Dashboard JWT secret',
  SUPABASE_URL: 'Supabase URL',
  SUPABASE_SERVICE_ROLE_KEY: 'Supabase service role key',
  REACT_APP_SUPABASE_URL: 'Supabase URL (client)',
  REACT_APP_SUPABASE_ANON_KEY: 'Supabase anon key (client)',
};

export default function DashboardConfigView({ onError, refreshToken }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    onError('');
    try {
      const res = await fetch(`${getApiBase()}/api/dashboard/env-status`, {
        headers: getDashboardAuthHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || `Error ${res.status}`);
      }
      setStatus(data);
    } catch (err) {
      setStatus(null);
      onError(err?.message || 'Failed to load config status');
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus, refreshToken]);

  return (
    <div className="card card-outline card-secondary">
      <div className="card-header">
        <h3 className="card-title">
          <i className="fas fa-cog mr-1" />
          Environment config status
        </h3>
        <div className="card-tools">
          <button
            type="button"
            className="btn btn-tool"
            onClick={loadStatus}
            disabled={loading}
            title="Refresh"
          >
            <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`} />
          </button>
        </div>
      </div>
      <div className="card-body">
        <p className="text-muted text-sm">
          Which environment variables are set. Values are never shown or editable here — update them in{' '}
          <code>.env</code> locally or in Vercel → Settings → Environment Variables.
        </p>
        {loading && !status ? (
          <p className="text-muted mb-0">
            <i className="fas fa-spinner fa-spin mr-1" />
            Loading…
          </p>
        ) : status ? (
          <>
            {status.hint && <p className="text-sm text-info">{status.hint}</p>}
            <table className="table table-striped table-sm mb-0">
              <thead>
                <tr>
                  <th>Variable</th>
                  <th className="text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(status.env || {}).map(([key, value]) => (
                  <tr key={key}>
                    <td>{ENV_LABELS[key] || key}</td>
                    <td className="text-right">
                      {value === 'set' ? (
                        <span className="badge badge-success">Set</span>
                      ) : (
                        <span className="badge badge-secondary">Not set</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : null}
      </div>
    </div>
  );
}
